import { useCallback, useMemo } from "react"
import {
  sanitizeSingleWord,
  sanitizeMultiWordTyping,
  sanitizeMultiWord,
  isSpaceKey,
  isDigitKey,
  shouldBlockSpaceForMultiWord,
  neutralizeDoubleSpacePeriod,
  sanitizeLettersSpacesTyping,
  sanitizeLettersSpaces
} from "utils/InputSanitizers"

// Hook to provide sanitized text input handlers for Ant Design Form fields
// variant: 'singleWord' | 'multiWord' | 'noDigits' | 'lettersSpaces' | 'singleWordNoDigits' | 'multiWordNoDigits'
export const useSanitizedTextInput = ({ form, field, variant = "singleWord" }) => {
  const onKeyDown = useCallback(
    (e) => {
      if (variant === "singleWord") {
        if (isSpaceKey(e)) e.preventDefault()
      } else if (variant === "singleWordNoDigits") {
        if (isSpaceKey(e)) e.preventDefault()
        // Only allow letters A-Z; block digits and symbols
        const key = e?.key || ""
        const isLetter = /^[A-Za-z]$/.test(key)
        if (!isLetter && key.length === 1) e.preventDefault()
      } else if (variant === "multiWord") {
        if (isSpaceKey(e)) {
          const input = e.target
          const selectionStart = input?.selectionStart ?? 0
          const value = input?.value || ""
          if (shouldBlockSpaceForMultiWord(value, selectionStart)) e.preventDefault()
        }
      } else if (variant === "multiWordNoDigits") {
        if (isDigitKey(e)) e.preventDefault()
        if (isSpaceKey(e)) {
          const input = e.target
          const selectionStart = input?.selectionStart ?? 0
          const value = input?.value || ""
          if (shouldBlockSpaceForMultiWord(value, selectionStart)) e.preventDefault()
        }
      } else if (variant === "noDigits") {
        if (isDigitKey(e)) e.preventDefault()
      } else if (variant === "lettersSpaces") {
        // Only allow letters and single spaces: block digits and special chars via keydown
        const key = e?.key || ""
        const isLetter = /^[A-Za-z]$/.test(key)
        if (!isLetter && !isSpaceKey(e) && key.length === 1) e.preventDefault()
        if (isSpaceKey(e)) {
          const input = e.target
          const selectionStart = input?.selectionStart ?? 0
          const value = input?.value || ""
          if (shouldBlockSpaceForMultiWord(value, selectionStart)) e.preventDefault()
        }
      }
    },
    [variant]
  )

  const onChange = useCallback(
    (e) => {
      const value = e?.target?.value ?? ""
      const neutralized = neutralizeDoubleSpacePeriod(value)
      let sanitized
      if (variant === "singleWord") {
        sanitized = sanitizeSingleWord(neutralized)
      } else if (variant === "singleWordNoDigits") {
        sanitized = sanitizeSingleWord(neutralized).replace(/[^A-Za-z]+/g, "")
      } else if (variant === "multiWord") {
        sanitized = sanitizeMultiWordTyping(neutralized)
      } else if (variant === "multiWordNoDigits") {
        sanitized = sanitizeMultiWordTyping(neutralized).replace(/\d+/g, "")
      } else if (variant === "noDigits") {
        // strip all digits, keep other characters unchanged
        sanitized = neutralized.replace(/\d+/g, "")
      } else if (variant === "lettersSpaces") {
        sanitized = sanitizeLettersSpacesTyping(neutralized)
      } else {
        sanitized = neutralized
      }
      if (sanitized !== value) form.setFieldsValue({ [field]: sanitized })
    },
    [form, field, variant]
  )

  const onPaste = useCallback(
    (e) => {
      e.preventDefault()
      const pasted = e?.clipboardData?.getData?.("text") || ""
      const neutralized = neutralizeDoubleSpacePeriod(pasted)
      let sanitized
      if (variant === "singleWord") {
        sanitized = sanitizeSingleWord(neutralized)
      } else if (variant === "singleWordNoDigits") {
        sanitized = sanitizeSingleWord(neutralized).replace(/[^A-Za-z]+/g, "")
      } else if (variant === "multiWord") {
        sanitized = sanitizeMultiWord(neutralized)
      } else if (variant === "multiWordNoDigits") {
        sanitized = sanitizeMultiWord(neutralized).replace(/\d+/g, "")
      } else if (variant === "noDigits") {
        sanitized = neutralized.replace(/\d+/g, "")
      } else if (variant === "lettersSpaces") {
        sanitized = sanitizeLettersSpaces(neutralized)
      } else {
        sanitized = neutralized
      }
      form.setFieldsValue({ [field]: sanitized })
    },
    [form, field, variant]
  )

  const onBlur = useCallback(() => {
    if (variant === "multiWord" || variant === "multiWordNoDigits") {
      const value = form.getFieldValue(field) || ""
      const normalized = sanitizeMultiWord(neutralizeDoubleSpacePeriod(value)).replace(/\d+/g, "")
      if (normalized !== value) form.setFieldsValue({ [field]: normalized })
    } else if (variant === "lettersSpaces") {
      const value = form.getFieldValue(field) || ""
      const normalized = sanitizeLettersSpaces(neutralizeDoubleSpacePeriod(value))
      if (normalized !== value) form.setFieldsValue({ [field]: normalized })
    }
  }, [form, field, variant])

  return useMemo(() => {
    const needsBlur = ["multiWord", "multiWordNoDigits", "lettersSpaces"].includes(variant)
    return needsBlur ? { onKeyDown, onChange, onPaste, onBlur } : { onKeyDown, onChange, onPaste }
  }, [onKeyDown, onChange, onPaste, onBlur, variant])
}
